import { Controller } from "@hotwired/stimulus";
import { Tooltip } from "@tabler/core/dist/js/tabler.js";

const DESKTOP_BREAKPOINT = "(min-width: 992px)";
const DESKTOP_BREAKPOINT_WIDTH = 992;
const MOBILE_OPEN_CLASS = "sidebar-mobile-open";
const DESKTOP_MINI_CLASS = "sidebar-desktop-mini";
const SCROLL_LOCK_CLASS = "sidebar-scroll-locked";
const DESKTOP_EXPANDED_ICON_CLASS = "ti-layout-sidebar-left-collapse";
const DESKTOP_COLLAPSED_ICON_CLASS = "ti-layout-sidebar-right-collapse";

export default class extends Controller {
  static targets = ["sidebar", "backdrop", "mobileToggle", "desktopToggle", "tooltipItem"];
  static values = { stateUrl: String };

  connect() {
    this.desktopMediaQuery = window.matchMedia(DESKTOP_BREAKPOINT);
    this.desktopMiniPreferred = this.isDesktopMini();
    this.onBreakpointChange = this.syncForBreakpoint.bind(this);
    this.onDocumentKeydown = this.closeOnEscape.bind(this);
    this.onTurboBeforeRender = this.syncTurboRenderBody.bind(this);

    this.desktopMediaQuery.addEventListener("change", this.onBreakpointChange);
    document.addEventListener("keydown", this.onDocumentKeydown);
    document.addEventListener("turbo:before-render", this.onTurboBeforeRender);

    this.syncForBreakpoint();
  }

  disconnect() {
    this.desktopMediaQuery?.removeEventListener("change", this.onBreakpointChange);
    document.removeEventListener("keydown", this.onDocumentKeydown);
    document.removeEventListener("turbo:before-render", this.onTurboBeforeRender);
    this.closeMobile();
    this.syncDesktopState();
  }

  toggleMobile(event) {
    event?.preventDefault();

    if (this.isMobileOpen()) {
      this.closeMobile(event);
      return;
    }

    this.openMobile();
  }

  openMobile() {
    this.element.classList.add(MOBILE_OPEN_CLASS);
    document.body.classList.add(SCROLL_LOCK_CLASS);
    this.syncBackdrop(true);
    this.syncMobileToggleState();
    this.syncDesktopState();
  }

  closeMobile(event) {
    event?.preventDefault();

    this.element.classList.remove(MOBILE_OPEN_CLASS);
    document.body.classList.remove(SCROLL_LOCK_CLASS);
    this.syncBackdrop(false);
    this.syncMobileToggleState();
    this.syncDesktopState();
  }

  toggleDesktop(event) {
    event?.preventDefault();

    this.element.classList.toggle(DESKTOP_MINI_CLASS);
    this.desktopMiniPreferred = this.isDesktopMini();
    this.syncDesktopState();
    this.pendingDesktopStateRequest = this.enqueueDesktopStatePersistence();
  }

  closeOnEscape(event) {
    if (event?.key !== "Escape" || !this.isMobileOpen()) return;

    event.preventDefault();
    this.closeMobile();
  }

  syncForBreakpoint() {
    this.element.classList.toggle(DESKTOP_MINI_CLASS, this.isDesktopBreakpoint() && this.desktopMiniPreferred);

    if (this.isDesktopBreakpoint()) {
      this.closeMobile();
    } else {
      const mobileOpen = this.isMobileOpen();

      document.body.classList.toggle(SCROLL_LOCK_CLASS, mobileOpen);
      this.syncBackdrop(mobileOpen);
    }

    this.syncMobileToggleState();
    this.syncDesktopState();
  }

  isDesktopBreakpoint() {
    return this.desktopMediaQuery?.matches === true || window.innerWidth >= DESKTOP_BREAKPOINT_WIDTH;
  }

  isMobileOpen() {
    return this.element.classList.contains(MOBILE_OPEN_CLASS);
  }

  isDesktopMini() {
    return this.element.classList.contains(DESKTOP_MINI_CLASS);
  }

  isIconOnlyDesktop() {
    return this.isDesktopBreakpoint() && this.isDesktopMini();
  }

  syncBackdrop(visible) {
    if (!this.hasBackdropTarget) return;

    this.backdropTarget.hidden = !visible;
    this.backdropTarget.setAttribute("aria-hidden", String(!visible));
  }

  syncMobileToggleState() {
    const expanded = String(this.isMobileOpen());

    this.mobileToggleTargets.forEach((toggle) => {
      toggle.setAttribute("aria-expanded", expanded);
    });
  }

  syncDesktopState() {
    this.syncDesktopToggleState();
    this.syncTooltipItems();
  }

  syncDesktopToggleState() {
    const expanded = String(!this.isDesktopMini());
    const labelKey = this.isIconOnlyDesktop() ? "sidebarOpenLabelValue" : "sidebarCloseLabelValue";
    const iconClass = this.isDesktopMini() ? DESKTOP_COLLAPSED_ICON_CLASS : DESKTOP_EXPANDED_ICON_CLASS;

    this.desktopToggleTargets.forEach((toggle) => {
      const label = toggle.dataset[labelKey];
      const icon = toggle.querySelector(".icon");

      toggle.setAttribute("aria-expanded", expanded);
      icon?.classList.remove(DESKTOP_EXPANDED_ICON_CLASS, DESKTOP_COLLAPSED_ICON_CLASS);
      icon?.classList.add(iconClass);

      if (!label) return;

      toggle.setAttribute("aria-label", label);
      toggle.setAttribute("title", label);
    });
  }

  syncTooltipItems() {
    if (this.isIconOnlyDesktop()) {
      this.tooltipItemTargets.forEach((item) => {
        this.enableTooltipItem(item);
      });
      return;
    }

    this.tooltipItemTargets.forEach((item) => {
      this.disableTooltipItem(item);
    });
  }

  enableTooltipItem(item) {
    const title = item.dataset.sidebarTooltipTitleValue;
    if (!title) return;

    this.disposeTooltip(item);
    item.setAttribute("title", title);
    item.setAttribute("data-bs-title", title);
    item.setAttribute("data-bs-toggle", "tooltip");
    item.setAttribute("data-bs-placement", "right");
    item.setAttribute("data-bs-container", "body");
    Tooltip.getOrCreateInstance(item);
    item.setAttribute("title", title);
  }

  disableTooltipItem(item) {
    this.disposeTooltip(item);
    item.removeAttribute("title");
    item.removeAttribute("data-bs-title");
    item.removeAttribute("data-bs-toggle");
    item.removeAttribute("data-bs-placement");
    item.removeAttribute("data-bs-container");
    item.removeAttribute("data-bs-original-title");
    item.removeAttribute("aria-describedby");
  }

  disposeTooltip(item) {
    Tooltip.getInstance(item)?.dispose();
  }

  syncTurboRenderBody(event) {
    event.detail?.newBody?.classList?.toggle(DESKTOP_MINI_CLASS, this.isDesktopMini());
  }

  persistDesktopState() {
    if (!this.hasStateUrlValue) return Promise.resolve();

    return fetch(this.stateUrlValue, {
      method: "PATCH",
      credentials: "same-origin",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken()
      },
      body: JSON.stringify({ desktop_mini: this.isDesktopMini() })
    }).catch(() => {});
  }

  enqueueDesktopStatePersistence() {
    const previousRequest = this.pendingDesktopStateRequest || Promise.resolve();
    const request = previousRequest.then(() => this.persistDesktopState(), () => this.persistDesktopState());

    request.finally(() => {
      if (this.pendingDesktopStateRequest === request) this.pendingDesktopStateRequest = null;
    });

    return request;
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
  }
}
